import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import {
  fetchPinsFromLibrary,
  deletePinFromLibrary,
  deletePinsFromLibrary,
  updatePinsStatus,
  fetchPinStatusCounts,
} from '../../services/pinterestPinService';
import {
  loadPinterestAccounts,
  loadPinterestBoards,
  callPublishPin,
  extractPinterestUrl,
  updatePinRecord,
} from '../../services/pinterestPublishService';
import { useModal } from '../../components/ui/ModalProvider';
import { generateHumanSchedule } from '../../utils/humanSchedule';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';

// ─── Utilities ───────────────────────────────────────────────────────────────

const padNumber = (v) => String(v).padStart(2, '0');

const toDatetimeLocal = (date = new Date()) =>
  `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}T${padNumber(date.getHours())}:${padNumber(date.getMinutes())}`;

const escapeCsvValue = (value = '') =>
  `"${String(value ?? '').replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;

const resolveBoardValue = (pin = {}) => {
  const nameCandidates = [
    pin?.pinterest_board?.name,
    pin?.pinterestBoard?.name,
    pin?.pinterest_board_name,
    pin?.board_name,
    pin?.publish_board?.name,
    pin?.publish_board_name,
    pin?.boardName,
  ];
  const idCandidates = [
    pin?.board,
    pin?.board_id,
    pin?.boardId,
    pin?.pinterest_board_id,
    pin?.pinterestBoard?.id,
    pin?.pinterest_board?.id,
    pin?.id,
  ];
  const normalizedName = nameCandidates.find((v) => typeof v === 'string' && v.trim());
  if (normalizedName) return normalizedName.trim();
  const normalizedId = idCandidates.find((v) => typeof v === 'string' && v.trim());
  if (normalizedId) return normalizedId.trim();
  return 'Unknown board';
};

const downloadCsvFile = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body?.appendChild(link);
  link.click();
  document.body?.removeChild(link);
  URL.revokeObjectURL(link.href);
};

const STATUS_STYLES = {
  ready:      'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  publishing: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  published:  'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  scheduled:  'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  failed:     'bg-red-50 text-red-700 ring-1 ring-red-200',
};

const STATUS_DOTS = {
  ready:      'bg-blue-500',
  publishing: 'bg-violet-500 animate-pulse',
  published:  'bg-emerald-500',
  scheduled:  'bg-amber-500',
  failed:     'bg-red-500',
};

const STATUS_LABELS = {
  ready:      'Ready',
  publishing: 'Publishing…',
  published:  'Published',
  scheduled:  'Scheduled',
  failed:     'Failed',
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── Component ───────────────────────────────────────────────────────────────

export default function PinLibrary() {
  const { alert, confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startPinsRequest = useRequestGuard();
  const startStatusCountsRequest = useRequestGuard();
  const startAccountsRequest = useRequestGuard();

  // ── State ──
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', search: '', startDate: '', endDate: '', pinterestAccountId: '' });
  const [selectedPins, setSelectedPins] = useState({});
  const [exportInterval, setExportInterval] = useState(122);
  const [pinsPerDay, setPinsPerDay] = useState(0);
  const [publishStart, setPublishStart] = useState(() => toDatetimeLocal(new Date(Date.now() + 60 * 60 * 1000)));
  const [isExporting, setIsExporting] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  const selectAllRef = useRef(null);
  // Prevents a double fetch when a filter change resets the page to 1
  const skipPageEffect = useRef(false);
  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  // Server-side total count (supports up to 600 pins)
  const [totalCount, setTotalCount] = useState(0);
  // Per-status counts fetched independently so filter tabs always show accurate numbers
  const [statusCounts, setStatusCounts] = useState({ all: 0, ready: 0, published: 0, scheduled: 0, failed: 0 });

  // ── Publishing setup state ──
  const [accounts, setAccounts] = useState([]);
  const [boards, setBoards] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedBoard, setSelectedBoard] = useState('');
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [boardsLoading, setBoardsLoading] = useState(false);

  // Per-pin transient states: { [pinId]: { loading, error, pinterestUrl } }
  const [pinStates, setPinStates] = useState({});

  // Bulk queue: null | { total, done, failed, currentTitle }
  const [bulkQueue, setBulkQueue] = useState(null);

  // Single-pin schedule modal
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [isSchedulingPin, setIsSchedulingPin] = useState(false);

  // Clear the previous profile's pins immediately on switch so stale
  // content can't flash while the new profile's data is loading.
  useEffect(() => {
    setPins([]);
    setTotalCount(0);
    setStatusCounts({ all: 0, ready: 0, published: 0, scheduled: 0, failed: 0 });
  }, [activeProfile?.id]);

  // ── Load pins when filters change (always reset to page 1) ──
  useEffect(() => {
    skipPageEffect.current = true;
    setCurrentPage(1);
    loadPins(1, filters);
    loadStatusCounts(filters);
  }, [filters, activeProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load pins when user navigates to a different page ──
  useEffect(() => {
    if (skipPageEffect.current) {
      skipPageEffect.current = false;
      return;
    }
    loadPins(currentPage, filters);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPins(page, currentFilters) {
    const isCurrent = startPinsRequest();
    setLoading(true);
    try {
      const scopedFilters = { ...currentFilters, profileId: activeProfile?.id || null };
      const { pins: data, totalCount: count } = await fetchPinsFromLibrary(scopedFilters, page, PAGE_SIZE);
      if (!isCurrent()) return;
      setPins(data);
      setTotalCount(count);
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Error loading pins:', err);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }

  async function loadStatusCounts(currentFilters) {
    const isCurrent = startStatusCountsRequest();
    try {
      const counts = await fetchPinStatusCounts({ ...currentFilters, profileId: activeProfile?.id || null });
      if (!isCurrent()) return;
      setStatusCounts(counts);
    } catch {}
  }

  // ── Load Pinterest accounts on mount ──
  useEffect(() => {
    const isCurrent = startAccountsRequest();
    loadPinterestAccounts(activeProfile?.id)
      .then((accs) => { if (isCurrent()) setAccounts(accs); })
      .catch(() => {})
      .finally(() => { if (isCurrent()) setAccountsLoading(false); });
  }, [activeProfile?.id, startAccountsRequest]);

  // ── Load boards when account changes ──
  useEffect(() => {
    if (!selectedAccount) {
      setBoards([]);
      setSelectedBoard('');
      return;
    }
    setBoardsLoading(true);
    loadPinterestBoards(selectedAccount)
      .then((bds) => setBoards(bds))
      .catch(() => setBoards([]))
      .finally(() => setBoardsLoading(false));
  }, [selectedAccount]);


  // ─── Helpers ─────────────────────────────────────────────────────────────

  const updatePinInState = (pinId, updates) => {
    setPins((prev) => prev.map((p) => (p.id === pinId ? { ...p, ...updates } : p)));
  };

  const resolvePublishBoard = (pin) => pin?.board_id || selectedBoard || '';

  async function safePinUpdate(pinId, updates) {
    try {
      await updatePinRecord(pinId, updates);
    } catch {
      try { await updatePinsStatus([pinId], updates.status); } catch {}
    }
  }

  function handlePageChange(page) {
    const clamped = Math.max(1, Math.min(page, totalPages));
    if (clamped !== currentPage) setCurrentPage(clamped);
  }

  // ─── Delete ──────────────────────────────────────────────────────────────

  async function handleDelete(pinId) {
    if (!(await confirm('Are you sure you want to delete this pin?'))) return;
    const deletedPin = pins.find((p) => p?.id === pinId);
    try {
      await deletePinFromLibrary(pinId);
      setPins((prev) => prev.filter((p) => p?.id !== pinId));
      setTotalCount((prev) => Math.max(0, prev - 1));
      if (deletedPin?.status) {
        setStatusCounts((prev) => ({
          ...prev,
          all: Math.max(0, prev.all - 1),
          [deletedPin.status]: Math.max(0, (prev[deletedPin.status] || 0) - 1),
        }));
      }
    } catch {
      alert('Failed to delete pin');
    }
  }

  async function handleDeleteSelected() {
    const toDelete = Object.values(selectedPins);
    if (!toDelete.length) { alert('Select at least one pin to delete.'); return; }

    const ok = await confirm(`Delete ${toDelete.length} pin${toDelete.length > 1 ? 's' : ''}? This cannot be undone.`);
    if (!ok) return;

    const idsToDelete = toDelete.map((p) => p?.id).filter(Boolean);
    setIsDeletingSelected(true);
    try {
      await deletePinsFromLibrary(idsToDelete);
      setPins((prev) => prev.filter((p) => !idsToDelete.includes(p?.id)));
      setTotalCount((prev) => Math.max(0, prev - idsToDelete.length));
      setStatusCounts((prev) => {
        const next = { ...prev, all: Math.max(0, prev.all - idsToDelete.length) };
        toDelete.forEach((pin) => {
          if (pin?.status) {
            next[pin.status] = Math.max(0, (next[pin.status] || 0) - 1);
          }
        });
        return next;
      });
      setSelectedPins({});
    } catch {
      alert('Failed to delete selected pins');
    } finally {
      setIsDeletingSelected(false);
    }
  }

  // ─── CSV Export ──────────────────────────────────────────────────────────

  async function handleExportCsv() {
    const selectedRows = Object.values(selectedPins);
    if (!selectedRows?.length) { alert('Select at least one pin before exporting.'); return; }
    const exportedPinIds = selectedRows.map((pin) => pin?.id).filter(Boolean);
    const intervalMinutes = Number(exportInterval) || 122;
    if (intervalMinutes < 1) { alert('Interval must be at least 1 minute.'); return; }
    const startDate = publishStart ? new Date(publishStart) : new Date();
    if (Number.isNaN(startDate.getTime())) { alert('Publish start date is invalid.'); return; }

    setIsExporting(true);
    try {
      const rows = selectedRows.map((pin, index) => {
        const publishDate = new Date(startDate.getTime() + intervalMinutes * 60_000 * index);
        const boardName = resolveBoardValue(pin);
        return [
          pin?.pin_title,
          pin?.image_url || pin?.imageUrl,
          boardName,
          '',
          pin?.pin_description,
          pin?.article_url || pin?.articleUrl,
          publishDate.toISOString().slice(0, 19),
          '',
        ].map(escapeCsvValue).join(',');
      });

      const csvContent = ['Title,Media URL,Pinterest board,Thumbnail,Description,Link,Publish date,Keywords', ...rows].join('\n');
      downloadCsvFile(csvContent, `pinterest-pins-${Date.now()}.csv`);

      if (exportedPinIds.length) {
        try {
          await updatePinsStatus(exportedPinIds, 'published');
          setPins((prev) => prev.map((pin) => exportedPinIds.includes(pin?.id) ? { ...pin, status: 'published' } : pin));
          setSelectedPins((prev) => {
            const next = { ...prev };
            exportedPinIds.forEach((id) => delete next[id]);
            return next;
          });
        } catch {}
      }
    } catch {
      alert('Failed to generate CSV export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }

  // ─── Publish Now (single) ─────────────────────────────────────────────────

  async function handlePublishNow(pin) {
    if (!selectedAccount) { alert('Select a Pinterest account in the Publishing Setup panel first.'); return; }
    const boardId = resolvePublishBoard(pin);
    if (!boardId) { alert('No board available. Select a default board in Publishing Setup, or ensure this pin was generated with a board.'); return; }

    setPinStates((prev) => ({ ...prev, [pin.id]: { loading: true } }));
    updatePinInState(pin.id, { status: 'publishing' });

    try {
      const result = await callPublishPin({ pin, accountId: pin?.pinterest_account_id || selectedAccount, boardId });
      const pinterestUrl = extractPinterestUrl(result);

      const updates = {
        status: 'published',
        published_at: new Date().toISOString(),
        ...(pinterestUrl ? { pinterest_pin_url: pinterestUrl } : {}),
      };
      updatePinInState(pin.id, updates);
      setPinStates((prev) => ({ ...prev, [pin.id]: { loading: false, pinterestUrl } }));
      await safePinUpdate(pin.id, updates);
    } catch (err) {
      const reason = err?.message || 'Unknown error';
      updatePinInState(pin.id, { status: 'failed', failure_reason: reason });
      setPinStates((prev) => ({ ...prev, [pin.id]: { loading: false, error: reason } }));
      await safePinUpdate(pin.id, { status: 'failed', failure_reason: reason });
    }
  }

  // ─── Schedule single pin ──────────────────────────────────────────────────

  async function handleConfirmSchedule() {
    if (!scheduleTarget || !scheduleDateTime) return;
    if (!selectedAccount) { alert('Select a Pinterest account in Publishing Setup first.'); return; }

    const scheduledDate = new Date(scheduleDateTime);
    if (isNaN(scheduledDate.getTime()) || scheduledDate < new Date(Date.now() + 9 * 60 * 1000)) {
      alert('Choose a date and time at least 10 minutes from now.');
      return;
    }

    const boardId = resolvePublishBoard(scheduleTarget);
    if (!boardId) { alert('No board available for this pin. Select a default board in Publishing Setup.'); return; }

    setIsSchedulingPin(true);
    try {
      const updates = {
        status: 'scheduled',
        scheduled_at: scheduledDate.toISOString(),
        pinterest_account_id: selectedAccount,
        board_id: boardId,
      };
      updatePinInState(scheduleTarget.id, updates);
      await safePinUpdate(scheduleTarget.id, updates);
      setScheduleTarget(null);
      setScheduleDateTime('');
    } catch (err) {
      alert(`Failed to schedule: ${err.message}`);
    } finally {
      setIsSchedulingPin(false);
    }
  }

  // ─── Bulk publish ─────────────────────────────────────────────────────────

  async function handlePublishSelected() {
    if (!selectedAccount) { alert('Select a Pinterest account in Publishing Setup first.'); return; }
    if (selectedPinCount > 4) {
      alert('You can publish a maximum of 4 pins at once. Please schedule the remaining pins instead.');
      return;
    }
    const toPublish = Object.values(selectedPins).filter((p) => p.status === 'ready' || p.status === 'failed');
    if (!toPublish.length) { alert('Select at least one Ready or Failed pin to publish.'); return; }

    const ok = await confirm(`Publish ${toPublish.length} pin${toPublish.length > 1 ? 's' : ''} now to Pinterest?`);
    if (!ok) return;

    setBulkQueue({ total: toPublish.length, done: 0, failed: 0, currentTitle: null });

    for (let i = 0; i < toPublish.length; i++) {
      const pin = toPublish[i];
      const boardId = resolvePublishBoard(pin);
      setBulkQueue((q) => ({ ...q, currentTitle: pin.pin_title }));
      updatePinInState(pin.id, { status: 'publishing' });

      try {
        const result = await callPublishPin({ pin, accountId: pin?.pinterest_account_id || selectedAccount, boardId: boardId || selectedBoard });
        const pinterestUrl = extractPinterestUrl(result);
        const updates = {
          status: 'published',
          published_at: new Date().toISOString(),
          ...(pinterestUrl ? { pinterest_pin_url: pinterestUrl } : {}),
        };
        updatePinInState(pin.id, updates);
        setPinStates((prev) => ({ ...prev, [pin.id]: { pinterestUrl } }));
        await safePinUpdate(pin.id, updates);
        setBulkQueue((q) => ({ ...q, done: q.done + 1 }));
      } catch (err) {
        const reason = err?.message || 'Unknown error';
        updatePinInState(pin.id, { status: 'failed', failure_reason: reason });
        await safePinUpdate(pin.id, { status: 'failed', failure_reason: reason });
        setBulkQueue((q) => ({ ...q, done: q.done + 1, failed: q.failed + 1 }));
      }

      if (i < toPublish.length - 1) await delay(800);
    }

    setBulkQueue((q) => ({ ...q, currentTitle: null }));
    setTimeout(() => setBulkQueue(null), 4000);
    setSelectedPins({});
  }

  // ─── Schedule selected with interval ─────────────────────────────────────

  async function handleScheduleSelected() {
    if (!selectedAccount) { alert('Select a Pinterest account in Publishing Setup first.'); return; }
    const toSchedule = Object.values(selectedPins).filter((p) => p.status === 'ready' || p.status === 'failed');
    if (!toSchedule.length) { alert('Select at least one Ready or Failed pin to schedule.'); return; }

    const startDate = publishStart ? new Date(publishStart) : new Date();
    if (isNaN(startDate.getTime())) { alert('Set a valid "Publish start date" above.'); return; }

    const effectiveIntervalMin = pinsPerDay > 0
      ? (24 * 60) / pinsPerDay
      : (Number(exportInterval) || 120);

    // Randomization happens once, here, at generation time — the resulting
    // timestamps are what gets saved; the publish cron never recomputes them.
    const schedule = generateHumanSchedule({
      platform: 'pinterest',
      count: toSchedule.length,
      baseDate: startDate,
      intervalMinutes: effectiveIntervalMin,
    });

    const scheduleDesc = pinsPerDay > 0
      ? `${pinsPerDay} pin${pinsPerDay > 1 ? 's' : ''}/day (every ~${Math.round(effectiveIntervalMin)} min)`
      : `every ~${exportInterval} min`;

    const previewLines = toSchedule
      .slice(0, 8)
      .map((pin, i) => `${schedule[i].toLocaleString()} — ${pin.pin_title || 'Untitled pin'}`)
      .join('\n');
    const previewMore = toSchedule.length > 8 ? `\n…and ${toSchedule.length - 8} more` : '';

    const ok = await confirm(
      `Schedule ${toSchedule.length} pin${toSchedule.length > 1 ? 's' : ''} — ${scheduleDesc}?\n\n${previewLines}${previewMore}`
    );
    if (!ok) return;

    setBulkQueue({ total: toSchedule.length, done: 0, failed: 0, currentTitle: 'Scheduling...' });

    for (let i = 0; i < toSchedule.length; i++) {
      const pin = toSchedule[i];
      const scheduledAt = schedule[i].toISOString();
      const boardId = resolvePublishBoard(pin);
      setBulkQueue((q) => ({ ...q, currentTitle: pin.pin_title }));

      try {
        const updates = {
          status: 'scheduled',
          scheduled_at: scheduledAt,
          pinterest_account_id: selectedAccount,
          board_id: boardId || selectedBoard,
        };
        updatePinInState(pin.id, updates);
        await safePinUpdate(pin.id, updates);
        setBulkQueue((q) => ({ ...q, done: q.done + 1 }));
      } catch (err) {
        const reason = err?.message || 'Unknown error';
        updatePinInState(pin.id, { status: 'failed', failure_reason: reason });
        await safePinUpdate(pin.id, { status: 'failed', failure_reason: reason });
        setBulkQueue((q) => ({ ...q, done: q.done + 1, failed: q.failed + 1 }));
      }

      if (i < toSchedule.length - 1) await delay(400);
    }

    setBulkQueue((q) => ({ ...q, currentTitle: null }));
    setTimeout(() => setBulkQueue(null), 4000);
    setSelectedPins({});
  }

  // ─── Derived values ───────────────────────────────────────────────────────

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const currentPagePins = pins || [];
  const rangeFrom = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeTo = Math.min(currentPage * PAGE_SIZE, totalCount);

  const selectedOnPageCount = currentPagePins?.filter((pin) => selectedPins[pin?.id])?.length || 0;
  const totalPinsOnPage = currentPagePins?.length || 0;
  const isAllOnPageSelected = totalPinsOnPage > 0 && selectedOnPageCount === totalPinsOnPage;
  const selectedPinCount = Object.keys(selectedPins).length;

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = selectedOnPageCount > 0 && selectedOnPageCount < totalPinsOnPage;
  }, [selectedOnPageCount, totalPinsOnPage]);

  const handleToggleSelectAll = () => {
    setSelectedPins((prev) => {
      const next = { ...prev };
      currentPagePins?.forEach((pin) => {
        if (!pin?.id) return;
        if (isAllOnPageSelected) delete next[pin.id];
        else next[pin.id] = pin;
      });
      return next;
    });
  };

  const handleTogglePinSelection = (pinId) => {
    const pin = pins.find((p) => p?.id === pinId);
    setSelectedPins((prev) => {
      const next = { ...prev };
      if (next[pinId]) delete next[pinId];
      else if (pin) next[pinId] = pin;
      return next;
    });
  };

  const isBusy = !!bulkQueue;
  const canPublish = !!selectedAccount && !isBusy;

  // ─── Shared pagination controls (rendered at top and bottom) ──────────────

  const PaginationBar = ({ className = '' }) => (
    <div className={`flex items-center gap-3 ${className}`}>
      <button
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
        className="h-8 px-3 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        ← Previous
      </button>
      <span className="text-xs text-gray-600 whitespace-nowrap">
        Page <span className="font-semibold text-gray-800">{currentPage}</span> of <span className="font-semibold text-gray-800">{totalPages || 1}</span>
      </span>
      <button
        disabled={currentPage === totalPages || totalPages === 0}
        onClick={() => handlePageChange(currentPage + 1)}
        className="h-8 px-3 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Next →
      </button>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />

        <main className="main-content">
          <div className="px-6 py-6 max-w-[1700px] mx-auto">

            {/* ── Header ── */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-1">
                  Pinterest / Pin Library
                </p>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Pin Library</h1>
              </div>
              <Link
                to="/pin/create"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New Pin
              </Link>
            </div>

            {/* ── Filters Block ── */}
            <div className="bg-white border border-gray-200/80 rounded-2xl shadow-sm p-5 mb-4">
              {/* Row: search + dates + status */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-[220px]">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                  <input
                    type="text"
                    value={filters?.search}
                    onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                    placeholder="Search pins by title or article…"
                    className="w-full h-10 pl-9 pr-4 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Pinterest Account filter */}
                <div className="relative min-w-[180px]">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E60023] pointer-events-none" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                  </svg>
                  <select
                    value={filters?.pinterestAccountId}
                    onChange={(e) => setFilters((prev) => ({ ...prev, pinterestAccountId: e.target.value }))}
                    className="w-full h-10 rounded-xl border border-gray-200 pl-9 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white appearance-none"
                  >
                    <option value="">{accountsLoading ? 'Loading…' : 'All Accounts'}</option>
                    {accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>{acc.username || 'Pinterest Account'}</option>
                    ))}
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Date from */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 whitespace-nowrap">From</span>
                  <input
                    type="date"
                    value={filters?.startDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                    className="h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Date to */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 whitespace-nowrap">To</span>
                  <input
                    type="date"
                    value={filters?.endDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                    className="h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Clear filters */}
                {(filters.search || filters.startDate || filters.endDate || filters.status || filters.pinterestAccountId) && (
                  <button
                    type="button"
                    onClick={() => setFilters({ status: '', search: '', startDate: '', endDate: '', pinterestAccountId: '' })}
                    className="h-10 px-3 rounded-xl border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors whitespace-nowrap"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {/* Status pills */}
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                {[
                  { key: '', label: 'All', count: statusCounts.all, dot: 'bg-gray-400', active: 'bg-gray-900 text-white shadow-sm', inactive: 'bg-gray-100 text-gray-600 hover:bg-gray-200' },
                  { key: 'ready', label: 'Ready', count: statusCounts.ready, dot: 'bg-blue-500', active: 'bg-blue-600 text-white shadow-sm', inactive: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
                  { key: 'published', label: 'Published', count: statusCounts.published, dot: 'bg-emerald-500', active: 'bg-emerald-600 text-white shadow-sm', inactive: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
                  { key: 'scheduled', label: 'Scheduled', count: statusCounts.scheduled, dot: 'bg-amber-500', active: 'bg-amber-500 text-white shadow-sm', inactive: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
                  { key: 'failed', label: 'Failed', count: statusCounts.failed, dot: 'bg-red-500', active: 'bg-red-600 text-white shadow-sm', inactive: 'bg-red-50 text-red-700 hover:bg-red-100' },
                ].map(({ key, label, count, dot, active, inactive }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilters((prev) => ({ ...prev, status: key }))}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${filters?.status === key ? active : inactive}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${filters?.status === key ? 'bg-current opacity-60' : dot}`} />
                    {label}
                    <span className={`text-xs font-semibold tabular-nums ${filters?.status === key ? 'opacity-75' : 'opacity-60'}`}>
                      {count ?? 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Publishing Control Center ── */}
            <div className="bg-white border border-gray-200/80 rounded-2xl shadow-sm mb-4 overflow-hidden">
              <div>

                {/* Settings */}
                <div className="p-5">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-4">
                    Publishing Settings
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                    {/* Account */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Pinterest Account</label>
                      <select
                        value={selectedAccount}
                        onChange={(e) => setSelectedAccount(e.target.value)}
                        className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                      >
                        <option value="">{accountsLoading ? 'Loading…' : 'Select account…'}</option>
                        {accounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>{acc.username || 'Pinterest Account'}</option>
                        ))}
                      </select>
                    </div>

                    {/* Board */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">
                        Default Board
                        <span className="ml-1 text-gray-400 font-normal">(optional)</span>
                      </label>
                      <select
                        value={selectedBoard}
                        onChange={(e) => setSelectedBoard(e.target.value)}
                        disabled={!selectedAccount || boardsLoading}
                        className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">{boardsLoading ? 'Loading…' : "Pin's own board"}</option>
                        {boards.map((b) => (
                          <option key={b.id} value={b.id}>{b.name || b.title || b.id}</option>
                        ))}
                      </select>
                    </div>

                    {/* Pins Per Day */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">
                        Pins Per Day
                        <span className="ml-1 text-gray-400 font-normal">(auto-interval)</span>
                      </label>
                      <select
                        value={pinsPerDay}
                        onChange={(e) => setPinsPerDay(Number(e.target.value))}
                        className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                      >
                        <option value={0}>Manual interval</option>
                        {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {n} pin{n > 1 ? 's' : ''}/day — every {Math.round((24 * 60) / n)} min
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Interval */}
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${pinsPerDay > 0 ? 'text-gray-400' : 'text-gray-500'}`}>
                        Posting Interval
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={pinsPerDay > 0 ? Math.round((24 * 60) / pinsPerDay) : exportInterval}
                          onChange={(e) => setExportInterval(Math.max(1, Number(e.target.value || 122)))}
                          disabled={pinsPerDay > 0}
                          className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-right focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                        />
                        <span className={`text-sm whitespace-nowrap ${pinsPerDay > 0 ? 'text-gray-400' : 'text-gray-500'}`}>min</span>
                      </div>
                      {pinsPerDay > 0 && (
                        <p className="text-[11px] text-indigo-500 mt-1 font-medium">
                          Auto-calculated from Pins Per Day
                        </p>
                      )}
                    </div>

                    {/* Start date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Publish Start Date</label>
                      <input
                        type="datetime-local"
                        value={publishStart}
                        onChange={(e) => setPublishStart(e.target.value || toDatetimeLocal())}
                        className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Connection status */}
                  <div className="mt-4 flex items-center gap-2">
                    {selectedAccount ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                        Account connected — ready to publish
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600">
                        <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
                        Select a Pinterest account to enable publishing
                      </span>
                    )}
                  </div>

                  {/* Bulk actions row */}
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      {selectedPinCount > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                          {selectedPinCount} selected
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={handlePublishSelected}
                        disabled={!canPublish || !selectedPinCount}
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                        title={!selectedAccount ? 'Select a Pinterest account first' : ''}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                        Publish Selected
                      </button>

                      <button
                        type="button"
                        onClick={handleScheduleSelected}
                        disabled={!canPublish || !selectedPinCount}
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                        title={!selectedAccount ? 'Select a Pinterest account first' : ''}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Schedule Selected
                      </button>

                      <button
                        type="button"
                        onClick={handleDeleteSelected}
                        disabled={!selectedPinCount || isDeletingSelected}
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {isDeletingSelected ? 'Deleting…' : 'Delete Selected'}
                      </button>

                      <button
                        type="button"
                        onClick={handleExportCsv}
                        disabled={isExporting || !selectedPinCount}
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {isExporting ? 'Exporting…' : 'Export CSV'}
                      </button>
                    </div>

                    {!selectedPinCount && (
                      <p className="text-xs text-gray-400 mt-3 text-center">
                        Check pins in the table to enable bulk actions
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Top Pagination Bar (inside Publishing Control Center, full-width bottom row) ── */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/40">
                  <p className="text-xs text-gray-500">
                    Showing{' '}
                    <span className="font-semibold text-gray-700">{rangeFrom}–{rangeTo}</span>
                    {' '}of{' '}
                    <span className="font-semibold text-gray-700">{totalCount}</span>{' '}pins
                  </p>
                  <PaginationBar />
                </div>
              )}
            </div>

            {/* ── Bulk Queue Progress ── */}
            {bulkQueue && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-3">
                    <svg className="animate-spin w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span className="text-sm font-semibold text-indigo-800">
                      {bulkQueue.currentTitle ? 'Processing' : 'Complete'} — {bulkQueue.done} / {bulkQueue.total} pins
                      {bulkQueue.failed > 0 && (
                        <span className="text-red-500 ml-2 font-medium">({bulkQueue.failed} failed)</span>
                      )}
                    </span>
                  </div>
                  {bulkQueue.currentTitle && (
                    <p className="text-xs text-indigo-600 truncate max-w-sm ml-4">{bulkQueue.currentTitle}</p>
                  )}
                </div>
                <div className="w-full bg-indigo-100 rounded-full h-1.5">
                  <div
                    className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${(bulkQueue.done / bulkQueue.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* ── Table ── */}
            {loading ? (
              <div className="bg-white border border-gray-200/80 rounded-2xl shadow-sm p-16 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-100 border-t-indigo-600 animate-spin mb-5" />
                <p className="text-sm font-medium text-gray-500">Loading your pins…</p>
              </div>
            ) : totalCount === 0 ? (
              <div className="bg-white border border-gray-200/80 rounded-2xl shadow-sm p-16 flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4 text-2xl">📌</div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">No pins found</h2>
                <p className="text-sm text-gray-500 mb-6">No pins match your current filters.</p>
                <Link to="/pin/create" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Create Pin
                </Link>
              </div>
            ) : (
              <div className="bg-white border border-gray-200/80 rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-200">
                        <th className="px-5 py-3.5 text-left">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Image</span>
                        </th>
                        <th className="px-5 py-3.5 text-left w-[30%]">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Pin Title</span>
                        </th>
                        <th className="px-5 py-3.5 text-left">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Article</span>
                        </th>
                        <th className="px-5 py-3.5 text-center w-10">
                          <input
                            ref={selectAllRef}
                            type="checkbox"
                            checked={isAllOnPageSelected}
                            onChange={handleToggleSelectAll}
                            className="h-4 w-4 cursor-pointer rounded accent-indigo-600"
                            aria-label="Select all visible pins"
                          />
                        </th>
                        <th className="px-5 py-3.5 text-left">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Account</span>
                        </th>
                        <th className="px-5 py-3.5 text-left">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Status</span>
                        </th>
                        <th className="px-5 py-3.5 text-left">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Created</span>
                        </th>
                        <th className="px-5 py-3.5 text-left">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pins?.map((pin) => {
                        const pinExtra = pinStates[pin?.id] || {};
                        const isPublishingThis = pinExtra.loading;
                        const effectiveStatus = isPublishingThis ? 'publishing' : pin?.status;
                        const pinterestUrl = pin?.pinterest_pin_url || pinExtra.pinterestUrl;
                        const failureReason = pin?.failure_reason || pinExtra.error;
                        const pinAccount = accounts.find((account) => String(account.id) === String(pin?.pinterest_account_id));

                        return (
                          <tr key={pin?.id} className="hover:bg-gray-50/60 transition-colors">

                            {/* Image */}
                            <td className="px-5 py-4">
                              <img
                                src={pin?.image_url}
                                alt={pin?.pin_title}
                                className="w-14 h-20 object-cover rounded-xl shadow-sm ring-1 ring-gray-200"
                              />
                            </td>

                            {/* Title + description */}
                            <td className="px-5 py-4 w-[30%]">
                              <p className="text-sm font-semibold text-gray-900 leading-snug">{pin?.pin_title}</p>
                              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed line-clamp-2">{pin?.pin_description}</p>
                            </td>

                            {/* Article */}
                            <td className="px-5 py-4">
                              <a
                                href={pin?.article_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                              >
                                View Article
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            </td>

                            {/* Checkbox */}
                            <td className="px-5 py-4 text-center">
                              <input
                                type="checkbox"
                                checked={!!selectedPins[pin?.id]}
                                onChange={() => handleTogglePinSelection(pin?.id)}
                                className="h-4 w-4 cursor-pointer rounded accent-indigo-600"
                                aria-label={`Select ${pin?.pin_title}`}
                              />
                            </td>

                            {/* Account saved with this pin */}
                            <td className="px-5 py-4">
                              <span className="text-sm text-gray-700">{pinAccount?.username || '—'}</span>
                            </td>

                            {/* Status */}
                            <td className="px-5 py-4">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[effectiveStatus] || 'bg-gray-100 text-gray-600'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[effectiveStatus] || 'bg-gray-400'}`} />
                                {STATUS_LABELS[effectiveStatus] || effectiveStatus}
                              </span>

                              {effectiveStatus === 'failed' && failureReason && (
                                <p className="text-xs text-red-500 mt-1.5 max-w-[150px] leading-tight" title={failureReason}>
                                  {failureReason.length > 50 ? failureReason.slice(0, 50) + '…' : failureReason}
                                </p>
                              )}
                              {effectiveStatus === 'published' && pin?.published_at && (
                                <p className="text-[11px] text-gray-400 mt-1.5 tabular-nums">
                                  {new Date(pin.published_at).toLocaleString()}
                                </p>
                              )}
                              {effectiveStatus === 'scheduled' && pin?.scheduled_at && (
                                <p className="text-[11px] text-amber-600 mt-1.5 tabular-nums font-medium">
                                  {new Date(pin.scheduled_at).toLocaleString()}
                                </p>
                              )}
                              {pin?.board_name && (
                                <p className="text-[11px] text-gray-400 mt-1">{pin.board_name}</p>
                              )}
                            </td>

                            {/* Created */}
                            <td className="px-5 py-4">
                              <p className="text-xs font-medium text-gray-700 tabular-nums">
                                {pin?.created_at ? new Date(pin.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                              </p>
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-4">
                              <div className="flex flex-wrap gap-1.5">
                                {/* Publish Now */}
                                {pin?.status === 'ready' && (
                                  <button
                                    type="button"
                                    onClick={() => handlePublishNow(pin)}
                                    disabled={isPublishingThis || !canPublish}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                                    title={!selectedAccount ? 'Select a Pinterest account first' : ''}
                                  >
                                    {isPublishingThis ? (
                                      <>
                                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                        Publishing…
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/></svg>
                                        Publish
                                      </>
                                    )}
                                  </button>
                                )}

                                {/* Retry */}
                                {pin?.status === 'failed' && (
                                  <button
                                    type="button"
                                    onClick={() => handlePublishNow(pin)}
                                    disabled={isPublishingThis || !canPublish}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                                  >
                                    {isPublishingThis ? (
                                      <>
                                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                        Retrying…
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                                        Retry
                                      </>
                                    )}
                                  </button>
                                )}

                                {/* Schedule */}
                                {pin?.status === 'ready' && (
                                  <button
                                    type="button"
                                    onClick={() => { setScheduleTarget(pin); setScheduleDateTime(''); }}
                                    disabled={!canPublish}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    title={!selectedAccount ? 'Select a Pinterest account first' : ''}
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                    Schedule
                                  </button>
                                )}

                                {/* View on Pinterest */}
                                {effectiveStatus === 'published' && pinterestUrl && (
                                  <a
                                    href={pinterestUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 transition-colors"
                                  >
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
                                    Pinterest
                                  </a>
                                )}

                                {/* View image */}
                                <a
                                  href={pin?.image_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                                  View
                                </a>

                                {/* Delete */}
                                <button
                                  type="button"
                                  onClick={() => handleDelete(pin?.id)}
                                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                  title="Delete pin"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Bottom Pagination ── */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/60">
                    <p className="text-xs text-gray-500">
                      Showing{' '}
                      <span className="font-semibold text-gray-700">{rangeFrom}–{rangeTo}</span>
                      {' '}of{' '}
                      <span className="font-semibold text-gray-700">{totalCount}</span>{' '}pins
                      <span className="ml-2 text-gray-400 tabular-nums">(page {currentPage} of {totalPages})</span>
                    </p>
                    <PaginationBar />
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        {/* ── Schedule Single Pin Modal ── */}
        {scheduleTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4 ring-1 ring-gray-200/50">
              {/* Modal header */}
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Schedule Pin</h3>
                  <p className="text-sm text-gray-500 mt-0.5 truncate max-w-[300px]">{scheduleTarget.pin_title}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setScheduleTarget(null); setScheduleDateTime(''); }}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>

              {/* Date input */}
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Publish Date & Time
              </label>
              <input
                type="datetime-local"
                value={scheduleDateTime}
                onChange={(e) => setScheduleDateTime(e.target.value)}
                min={toDatetimeLocal(new Date(Date.now() + 10 * 60 * 1000))}
                className="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-1"
              />
              <p className="text-xs text-gray-400 mb-4">Must be at least 10 minutes from now.</p>

              {scheduleTarget.board_name && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50 border border-gray-100 mb-5">
                  <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
                  <span className="text-xs text-gray-500">Board: <span className="font-semibold text-gray-700">{scheduleTarget.board_name}</span></span>
                  {selectedBoard && selectedBoard !== scheduleTarget.board_id && (
                    <span className="text-[11px] text-amber-600 ml-1">(overridden)</span>
                  )}
                </div>
              )}

              {/* Footer buttons */}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setScheduleTarget(null); setScheduleDateTime(''); }}
                  className="h-10 px-4 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSchedule}
                  disabled={!scheduleDateTime || isSchedulingPin}
                  className="h-10 px-5 rounded-xl bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2"
                >
                  {isSchedulingPin ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Scheduling…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                      Confirm Schedule
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarProvider>
  );
}
